import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { format, parseISO, differenceInDays } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { useLanguage } from '../context/LanguageContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog'
import { toast } from 'sonner'
import { cn } from '../lib/utils'
import {
  Bell, Plus, CreditCard, Car, Home, Wrench, Building2, MoreHorizontal,
  Calendar, DollarSign, RefreshCw, CheckCircle2, Clock, AlertTriangle,
  ChevronRight, Trash2, Edit, Loader2, X, ArrowLeft
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Category icons
const categoryIcons = {
  Tarjeta: CreditCard,
  Auto: Car,
  Casa: Home,
  Servicios: Wrench,
  Gobierno: Building2,
  Otros: MoreHorizontal
}

// Status colors
const statusStyles = {
  Pendiente: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/30' },
  Pagado: { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/30' },
  Vencido: { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/30' },
  Postergado: { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/30' }
}

// Urgency styles for badges
const urgencyStyles = {
  Vencido: { bg: 'bg-red-500', text: 'text-white' },
  Hoy: { bg: 'bg-red-500', text: 'text-white' },
  Alta: { bg: 'bg-amber-500', text: 'text-white' },
  Media: { bg: 'bg-blue-500', text: 'text-white' }
}

export default function RecordatoriosPage() {
  const { locale } = useLanguage()
  const lang = locale.startsWith('es') ? 'es' : 'en'
  const dateLocale = lang === 'es' ? es : enUS
  
  const [reminders, setReminders] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingReminder, setEditingReminder] = useState(null)
  const [selectedReminder, setSelectedReminder] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filter, setFilter] = useState('all')
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    type: 'Pago',
    category: 'Tarjeta',
    amount: '',
    currency: 'MXN',
    recurrence: 'Mensual',
    day_of_month: '',
    month: '',
    day: '',
    custom_interval_days: '',
    remind_time: '09:00',
    advance_days: [7, 3, 1, 0],
    notes: ''
  })

  const fetchReminders = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true)
    try {
      const response = await axios.get(`${API}/reminders`)
      setReminders(response.data)
    } catch (error) {
      console.error('Error fetching reminders:', error)
      // Try to init demo if empty
      if (error.response?.status === 404 || !error.response) {
        try {
          await axios.post(`${API}/reminders/init-demo`)
          const retryResponse = await axios.get(`${API}/reminders`)
          setReminders(retryResponse.data)
        } catch (demoError) {
          console.error('Error initializing demo:', demoError)
        }
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  // Manual refresh handler
  const handleRefresh = () => {
    fetchReminders(true)
  }

  useEffect(() => {
    fetchReminders()
    
    // Auto-refresh when user comes back to the page
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchReminders(false) // Silent refresh
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [fetchReminders])

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error(lang === 'es' ? 'El título es requerido' : 'Title is required')
      return
    }
    
    setSaving(true)
    try {
      const payload = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        day_of_month: formData.day_of_month ? parseInt(formData.day_of_month) : null,
        month: formData.month ? parseInt(formData.month) : null,
        day: formData.day ? parseInt(formData.day) : null,
        custom_interval_days: formData.custom_interval_days ? parseInt(formData.custom_interval_days) : null
      }
      
      if (editingReminder) {
        await axios.put(`${API}/reminders/${editingReminder.id}`, payload)
        toast.success(lang === 'es' ? 'Recordatorio actualizado' : 'Reminder updated')
      } else {
        await axios.post(`${API}/reminders`, payload)
        toast.success(lang === 'es' ? 'Recordatorio creado' : 'Reminder created')
      }
      
      setShowForm(false)
      setEditingReminder(null)
      resetForm()
      fetchReminders()
    } catch (error) {
      console.error('Error saving reminder:', error)
      toast.error(lang === 'es' ? 'Error al guardar' : 'Error saving')
    } finally {
      setSaving(false)
    }
  }

  const handlePay = async (reminder) => {
    try {
      await axios.post(`${API}/reminders/${reminder.id}/pay`)
      toast.success(lang === 'es' ? '¡Marcado como pagado!' : 'Marked as paid!')
      fetchReminders()
      setSelectedReminder(null)
    } catch (error) {
      toast.error(lang === 'es' ? 'Error al marcar' : 'Error marking')
    }
  }

  const handlePostpone = async (reminder, days) => {
    try {
      await axios.post(`${API}/reminders/${reminder.id}/postpone`, { days })
      toast.success(lang === 'es' ? `Postergado ${days} día(s)` : `Postponed ${days} day(s)`)
      fetchReminders()
      setSelectedReminder(null)
    } catch (error) {
      toast.error(lang === 'es' ? 'Error al postergar' : 'Error postponing')
    }
  }

  const handleDelete = async (reminder) => {
    if (!window.confirm(lang === 'es' ? '¿Eliminar este recordatorio?' : 'Delete this reminder?')) return
    
    try {
      await axios.delete(`${API}/reminders/${reminder.id}`)
      toast.success(lang === 'es' ? 'Recordatorio eliminado' : 'Reminder deleted')
      fetchReminders()
      setSelectedReminder(null)
    } catch (error) {
      toast.error(lang === 'es' ? 'Error al eliminar' : 'Error deleting')
    }
  }

  const handleEdit = (reminder) => {
    setEditingReminder(reminder)
    setFormData({
      title: reminder.title,
      type: reminder.type,
      category: reminder.category,
      amount: reminder.amount?.toString() || '',
      currency: reminder.currency || 'MXN',
      recurrence: reminder.recurrence,
      day_of_month: reminder.day_of_month?.toString() || '',
      month: reminder.month?.toString() || '',
      day: reminder.day?.toString() || '',
      custom_interval_days: reminder.custom_interval_days?.toString() || '',
      remind_time: reminder.remind_time || '09:00',
      advance_days: reminder.advance_days || [7, 3, 1, 0],
      notes: reminder.notes || ''
    })
    setShowForm(true)
    setSelectedReminder(null)
  }

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'Pago',
      category: 'Tarjeta',
      amount: '',
      currency: 'MXN',
      recurrence: 'Mensual',
      day_of_month: '',
      month: '',
      day: '',
      custom_interval_days: '',
      remind_time: '09:00',
      advance_days: [7, 3, 1, 0],
      notes: ''
    })
  }

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return 999
    try {
      const date = parseISO(dateStr)
      return differenceInDays(date, new Date())
    } catch {
      return 999
    }
  }

  const getUrgency = (daysUntil) => {
    if (daysUntil < 0) return 'Vencido'
    if (daysUntil === 0) return 'Hoy'
    if (daysUntil <= 3) return 'Alta'
    return 'Media'
  }

  const filteredReminders = reminders.filter(r => {
    if (filter === 'all') return true
    if (filter === 'active') return r.active && r.status !== 'Pagado'
    if (filter === 'vencido') return r.status === 'Vencido' || getDaysUntil(r.next_due_at) < 0
    return r.status === filter
  })

  if (loading) {
    return (
      <div className="p-4 md:p-8 pb-safe flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 pb-safe max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-amber-500" />
            {lang === 'es' ? 'Recordatorios' : 'Reminders'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {lang === 'es' ? 'Pagos y vencimientos' : 'Payments and due dates'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-full"
            data-testid="refresh-reminders-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            onClick={() => { resetForm(); setEditingReminder(null); setShowForm(true) }}
            className="rounded-full bg-amber-500 hover:bg-amber-600"
            data-testid="new-reminder-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Nuevo' : 'New'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { value: 'all', label: lang === 'es' ? 'Todos' : 'All' },
          { value: 'active', label: lang === 'es' ? 'Activos' : 'Active' },
          { value: 'vencido', label: lang === 'es' ? 'Vencidos' : 'Overdue' },
          { value: 'Pagado', label: lang === 'es' ? 'Pagados' : 'Paid' },
        ].map(f => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.value)}
            className="rounded-full whitespace-nowrap"
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Reminders List */}
      <div className="space-y-3">
        {filteredReminders.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Bell className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">
                {lang === 'es' ? 'No hay recordatorios' : 'No reminders'}
              </p>
              <Button
                variant="link"
                onClick={() => { resetForm(); setShowForm(true) }}
                className="mt-2"
              >
                {lang === 'es' ? 'Crear uno nuevo' : 'Create a new one'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredReminders.map(reminder => {
            const CategoryIcon = categoryIcons[reminder.category] || MoreHorizontal
            const daysUntil = getDaysUntil(reminder.next_due_at)
            const urgency = getUrgency(daysUntil)
            const statusStyle = statusStyles[reminder.status] || statusStyles.Pendiente
            const urgencyStyle = urgencyStyles[urgency]
            
            return (
              <Card 
                key={reminder.id}
                className={cn(
                  "cursor-pointer transition-all hover:shadow-md",
                  "border-l-4",
                  statusStyle.border
                )}
                onClick={() => setSelectedReminder(reminder)}
                data-testid={`reminder-card-${reminder.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                      statusStyle.bg
                    )}>
                      <CategoryIcon className={cn("w-5 h-5", statusStyle.text)} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-medium truncate">{reminder.title}</h3>
                        {urgency && (
                          <Badge className={cn("text-xs flex-shrink-0", urgencyStyle?.bg, urgencyStyle?.text)}>
                            {daysUntil < 0 
                              ? (lang === 'es' ? `${Math.abs(daysUntil)}d vencido` : `${Math.abs(daysUntil)}d overdue`)
                              : daysUntil === 0 
                                ? (lang === 'es' ? 'Hoy' : 'Today')
                                : `${daysUntil}d`
                            }
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <span>{reminder.type}</span>
                        <span>•</span>
                        <span>{reminder.recurrence}</span>
                        {reminder.amount && (
                          <>
                            <span>•</span>
                            <span className="font-medium text-foreground">
                              ${reminder.amount.toLocaleString()} {reminder.currency}
                            </span>
                          </>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        <span>
                          {reminder.next_due_at && format(parseISO(reminder.next_due_at), 'dd MMM yyyy', { locale: dateLocale })}
                        </span>
                      </div>
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            )
          })
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selectedReminder} onOpenChange={() => setSelectedReminder(null)}>
        <DialogContent className="sm:max-w-md">
          {selectedReminder && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const Icon = categoryIcons[selectedReminder.category] || MoreHorizontal
                    return <Icon className="w-5 h-5 text-amber-500" />
                  })()}
                  {selectedReminder.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Status Badge */}
                <div className="flex items-center justify-between">
                  <Badge className={cn(
                    statusStyles[selectedReminder.status]?.bg,
                    statusStyles[selectedReminder.status]?.text
                  )}>
                    {selectedReminder.status}
                  </Badge>
                  {selectedReminder.amount && (
                    <span className="text-xl font-bold">
                      ${selectedReminder.amount.toLocaleString()} {selectedReminder.currency}
                    </span>
                  )}
                </div>
                
                {/* Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{lang === 'es' ? 'Tipo' : 'Type'}</p>
                    <p className="font-medium">{selectedReminder.type}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{lang === 'es' ? 'Categoría' : 'Category'}</p>
                    <p className="font-medium">{selectedReminder.category}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{lang === 'es' ? 'Recurrencia' : 'Recurrence'}</p>
                    <p className="font-medium">{selectedReminder.recurrence}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{lang === 'es' ? 'Próximo vencimiento' : 'Next due'}</p>
                    <p className="font-medium">
                      {selectedReminder.next_due_at && format(parseISO(selectedReminder.next_due_at), 'dd MMM yyyy', { locale: dateLocale })}
                    </p>
                  </div>
                </div>
                
                {selectedReminder.notes && (
                  <div className="pt-2 border-t">
                    <p className="text-sm text-muted-foreground">{lang === 'es' ? 'Notas' : 'Notes'}</p>
                    <p className="text-sm mt-1">{selectedReminder.notes}</p>
                  </div>
                )}
                
                {/* Postpone Options */}
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-2">{lang === 'es' ? 'Posponer' : 'Postpone'}</p>
                  <div className="flex gap-2">
                    {[1, 3, 7].map(days => (
                      <Button
                        key={days}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePostpone(selectedReminder, days)}
                        className="flex-1"
                      >
                        +{days}d
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleEdit(selectedReminder)}
                  className="w-full sm:w-auto"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  {lang === 'es' ? 'Editar' : 'Edit'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto text-destructive hover:text-destructive"
                  onClick={() => handleDelete(selectedReminder)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {lang === 'es' ? 'Eliminar' : 'Delete'}
                </Button>
                <Button
                  onClick={() => handlePay(selectedReminder)}
                  className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                  data-testid="mark-paid-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {lang === 'es' ? 'Marcar Pagado' : 'Mark Paid'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Form Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              {editingReminder 
                ? (lang === 'es' ? 'Editar Recordatorio' : 'Edit Reminder')
                : (lang === 'es' ? 'Nuevo Recordatorio' : 'New Reminder')
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label>{lang === 'es' ? 'Título' : 'Title'} *</Label>
              <Input
                placeholder={lang === 'es' ? 'Ej: Pago Tarjeta BBVA' : 'Ex: Credit Card Payment'}
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                data-testid="reminder-title-input"
              />
            </div>
            
            {/* Type & Category */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{lang === 'es' ? 'Tipo' : 'Type'}</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData(prev => ({ ...prev, type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pago">{lang === 'es' ? 'Pago' : 'Payment'}</SelectItem>
                    <SelectItem value="Vencimiento">{lang === 'es' ? 'Vencimiento' : 'Expiration'}</SelectItem>
                    <SelectItem value="Trámite">{lang === 'es' ? 'Trámite' : 'Procedure'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{lang === 'es' ? 'Categoría' : 'Category'}</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tarjeta">{lang === 'es' ? 'Tarjeta' : 'Card'}</SelectItem>
                    <SelectItem value="Auto">{lang === 'es' ? 'Auto' : 'Car'}</SelectItem>
                    <SelectItem value="Casa">{lang === 'es' ? 'Casa' : 'Home'}</SelectItem>
                    <SelectItem value="Servicios">{lang === 'es' ? 'Servicios' : 'Services'}</SelectItem>
                    <SelectItem value="Gobierno">{lang === 'es' ? 'Gobierno' : 'Government'}</SelectItem>
                    <SelectItem value="Otros">{lang === 'es' ? 'Otros' : 'Others'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Amount & Currency */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{lang === 'es' ? 'Monto (opcional)' : 'Amount (optional)'}</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{lang === 'es' ? 'Moneda' : 'Currency'}</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">MXN</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Recurrence */}
            <div className="space-y-2">
              <Label>{lang === 'es' ? 'Recurrencia' : 'Recurrence'}</Label>
              <Select value={formData.recurrence} onValueChange={(v) => setFormData(prev => ({ ...prev, recurrence: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Semanal">{lang === 'es' ? 'Semanal' : 'Weekly'}</SelectItem>
                  <SelectItem value="Quincenal">{lang === 'es' ? 'Quincenal' : 'Biweekly'}</SelectItem>
                  <SelectItem value="Mensual">{lang === 'es' ? 'Mensual' : 'Monthly'}</SelectItem>
                  <SelectItem value="Semestral">{lang === 'es' ? 'Semestral' : 'Semi-annual'}</SelectItem>
                  <SelectItem value="Anual">{lang === 'es' ? 'Anual' : 'Annual'}</SelectItem>
                  <SelectItem value="Personalizada">{lang === 'es' ? 'Personalizada' : 'Custom'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Dynamic fields based on recurrence */}
            {(formData.recurrence === 'Mensual' || formData.recurrence === 'Semestral') && (
              <div className="space-y-2">
                <Label>{lang === 'es' ? 'Día del mes' : 'Day of month'}</Label>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  placeholder="1-31"
                  value={formData.day_of_month}
                  onChange={(e) => setFormData(prev => ({ ...prev, day_of_month: e.target.value }))}
                />
              </div>
            )}
            
            {formData.recurrence === 'Anual' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{lang === 'es' ? 'Mes' : 'Month'}</Label>
                  <Select value={formData.month} onValueChange={(v) => setFormData(prev => ({ ...prev, month: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder={lang === 'es' ? 'Selecciona' : 'Select'} />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {format(new Date(2026, i, 1), 'MMMM', { locale: dateLocale })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{lang === 'es' ? 'Día' : 'Day'}</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    placeholder="1-31"
                    value={formData.day}
                    onChange={(e) => setFormData(prev => ({ ...prev, day: e.target.value }))}
                  />
                </div>
              </div>
            )}
            
            {formData.recurrence === 'Personalizada' && (
              <div className="space-y-2">
                <Label>{lang === 'es' ? 'Intervalo en días' : 'Interval in days'}</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder={lang === 'es' ? 'Ej: 45' : 'Ex: 45'}
                  value={formData.custom_interval_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, custom_interval_days: e.target.value }))}
                />
              </div>
            )}
            
            {/* Remind Time */}
            <div className="space-y-2">
              <Label>{lang === 'es' ? 'Hora de recordatorio' : 'Reminder time'}</Label>
              <Input
                type="time"
                value={formData.remind_time}
                onChange={(e) => setFormData(prev => ({ ...prev, remind_time: e.target.value }))}
              />
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>{lang === 'es' ? 'Notas' : 'Notes'}</Label>
              <Textarea
                placeholder={lang === 'es' ? 'Notas adicionales...' : 'Additional notes...'}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="save-reminder-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              {lang === 'es' ? 'Guardar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
